// The ESV's own reading of the chapter. `/api/passage/:ref/audio` redirects to
// Crossway's mp3, so the audio streams from them and our key stays here.
//
// The element is only created on the first press: loading it with the page
// would spend one of our ESV requests on every chapter view, for a button most
// readers never touch.
import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";
import { track } from "@/lib/events";

export function AudioPlayer({ osis, display }: { osis: string; display: string }) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "playing" | "paused" | "error">("idle");

  // A different chapter is a different recording; drop the old one.
  useEffect(() => {
    return () => {
      audio.current?.pause();
      audio.current = null;
      setState("idle");
    };
  }, [osis]);

  const toggle = () => {
    if (audio.current) {
      if (audio.current.paused) {
        setState("loading");
        void audio.current.play().then(
          () => {
            setState("playing");
            track({ type: "audio_play", ref: osis });
          },
          () => setState("error"),
        );
      } else {
        audio.current.pause();
        setState("paused");
      }
      return;
    }
    const el = new Audio(`/api/passage/${encodeURIComponent(osis)}/audio`);
    el.addEventListener("ended", () => setState("paused"));
    el.addEventListener("error", () => setState("error"));
    audio.current = el;
    setState("loading");
    void el.play().then(
      () => {
        setState("playing");
        track({ type: "audio_play", ref: osis });
      },
      () => setState("error"),
    );
  };

  const label =
    state === "error" ? "Audio unavailable" : state === "playing" ? `Pause ${display}` : `Listen to ${display}`;

  return (
    <button
      onClick={toggle}
      disabled={state === "error"}
      aria-label={label}
      title={label}
      className="flex items-center gap-1.5 rounded-md border border-rule px-2.5 py-1 font-sans text-[11px] tracking-[0.06em] text-ink-2 uppercase hover:border-ink-3 hover:text-ink disabled:opacity-50"
    >
      <Icon name={state === "playing" ? "pause" : "play"} width="13" height="13" />
      {state === "error" ? "No audio" : state === "loading" ? "Loading…" : state === "playing" ? "Pause" : "Listen"}
    </button>
  );
}
